import { AppHeader, AppHeaderBackButton, Button, Form, Container } from '@ace-clublabs/shared-component-library';
import { PersonalInfo, ContactMethodInfo } from '../components/ContactDetails';
import { CindyBubble } from '../components/CindyBubble';
import { setGlobalState, useGlobalState } from '../state/state';
import { sources } from '../api/sources';
import { MSSMobileAuthStatus, MSSPhone, IChangeContactInfoRequest, MSSMembers } from '@lib/types';
import { IMemberDetails } from '../state/model';
import { ExistingMemberEmail, GenderType, PhoneType } from '@ace-clublabs/common-membership-api-types';
import { useHistory } from 'react-router-dom';
import { useLayoutEffect, useState } from 'react';
import { LoadingView } from '../components/LoadingView/LoadingView';
import { Pages } from '../utils/constants';
import { UtagHelper, UtagType } from '../utils/UtagHelper';

interface IContactInfoFormData {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  dateOfBirth: any; // this is a dayjs object
  gender: {
    genderName: string;
    genderValue: string;
  };
  emailAddress: string;
  mobilePhone: string;
  homePhone: string;
  // isHomePhoneDeclaredAsMobile possibly undefined if home phone is NOT entered
  isHomePhoneDeclaredAsMobile: boolean | undefined;
  CLMobileConsent: string | undefined;
  HMMobileConsent: 'agree' | 'disagree' | undefined;
}

export const ContactInfo: React.FC = () => {
  const [memberDetails] = useGlobalState('memberDetails');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [tags] = useGlobalState('tags');
  const [formData] = useGlobalState('formData');
  const history = useHistory();
  const urlParams = new URLSearchParams(window.location.search);
  const familyMemberCustomerId = urlParams.get('custId') || ''; //only family members will include custId query param
  const isFamilyMemberContact = familyMemberCustomerId ? true : false;
  const { sessionId, clubCode } = memberDetails;
  const [loading, setLoading] = useState(false);
  const [apiResponseError, setApiResponseError] = useState(false);
  const [finishLoadingProps, setFinishLoadingProps] = useState({ finished: false, onFinish: () => {} });
  const [formHasChanged, setFormHasChanged] = useState(false);
  const [isTagging, setTagging] = useState(true);
  useLayoutEffect(() => {
    UtagHelper.sendTag(UtagType.view, tags, 'edit member info');
    setTagging(false);
  }, []);

  const handleFormHasChanged = () => {
    setFormHasChanged(true);
  };

  const handleOnSubmitContactInfoChange = async (onSubmitFormData: IContactInfoFormData) => {
    try {
      const { firstName, lastName, middleName, gender } = onSubmitFormData;

      const phone = getPhones(onSubmitFormData, memberDetails, isFamilyMemberContact, familyMemberCustomerId);
      const email = getEmail(onSubmitFormData, memberDetails, isFamilyMemberContact, familyMemberCustomerId);

      const suffix = onSubmitFormData?.suffix || '';
      const customerId = familyMemberCustomerId || memberDetails.loggedInCustomerId;

      const changeContactInfoRequest = {
        sessionId: sessionId || '',
        contactInfo: {
          clubCode: clubCode || '',
          email,
          memberInfo: {
            firstName,
            lastName,
            middleInitial: middleName?.[0]?.toUpperCase() || '',
            birthDate: onSubmitFormData?.dateOfBirth?.format('MM-DD-YYYY') || '',
            suffix: suffix,
            gender: gender.genderValue,
          },
          customerId: isFamilyMemberContact ? familyMemberCustomerId : (memberDetails.loggedInCustomerId as string),
          phone,
        },
      };

      setLoading(true);
      setApiResponseError(false);
      const changeContactInfoResponse = await sources.changeContactInfo(changeContactInfoRequest);

      const success = changeContactInfoResponse?.status === 'success';

      if (!success) {
        setLoading(false);
        setApiResponseError(true);
        return;
      }

      let loggedInMember = { ...memberDetails.loggedInMember };

      if (!isFamilyMemberContact) {
        // if logged in user is updating themselves, we need to update the logged in member state
        // in addition to the members arrays
        loggedInMember = {
          ...loggedInMember,
          firstName,
          lastName,
          emailAddress: email.emailAddress,
          gender: gender.genderValue as GenderType,
          phoneNumbers: phone,
          middleName,
          dateOfBirth: onSubmitFormData?.dateOfBirth?.format('MM/DD/YYYY') || '',
          nameSuffix: suffix,
        };
      }

      const updatedMembers = memberDetails?.members?.map((mem) => {
        if (customerId === mem.customerId) {
          return {
            ...mem,
            firstName,
            lastName,
            emailAddress: email.emailAddress,
            gender: gender.genderValue,
            phoneNumbers: phone,
            middleName,
            dateOfBirth: onSubmitFormData?.dateOfBirth?.format('MM/DD/YYYY') || '',
            nameSuffix: suffix,
          };
        }
        return mem;
      }) as MSSMembers[];

      setGlobalState('memberDetails', {
        ...memberDetails,
        members: updatedMembers,
        loggedInMember: loggedInMember as MSSMembers,
      });

      setGlobalState('formData', {
        ...formData,
        members: updatedMembers,
      });

      setFinishLoadingProps({ finished: true, onFinish: history.goBack });
    } catch (error) {
      history.push(Pages.SorryPage);
    }
  };

  if (loading) {
    return (
      <LoadingView estimatedTimeInSeconds={20} finishProps={finishLoadingProps} message="Processing your request" />
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <Container maxWidth="sm" sx={{ padding: { lg: 0 } }}>
      <Form onSubmit={handleOnSubmitContactInfoChange} sx={{ marginBottom: '2.75rem' }} onChange={handleFormHasChanged}>
        <AppHeader backButton={!isMobileApp && <AppHeaderBackButton id="app-header-back-button" />} />
        <PersonalInfo hasError={apiResponseError} setFormHasChanged={handleFormHasChanged} />
        {isFamilyMemberContact ? <></> : <ContactMethodInfo setFormHasChanged={handleFormHasChanged} />}

        <Button
          id="contactDetailsSaveBtn"
          type="submit"
          className="btn-large"
          label="Save"
          fullWidth
          sx={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}
          disabled={!formHasChanged ? true : undefined}
        />

        <CindyBubble
          message="If you need to make membership changes that are not available online, please call us."
          className="contact-info-page-cindy"
          buttons={[
            {
              id: 'contact-info-cindy-call-button',
              buttonTitle: 'Call 1-844-786-1615',
              onClick: () => {
                window.location.href = 'tel:+18447861615';
              },
            },
          ]}
        />
      </Form>
    </Container>
  );
};

function getEmail(
  data: IContactInfoFormData,
  memberDetails: IMemberDetails,
  isFamilyMemberContact: boolean,
  custId?: string
): ExistingMemberEmail {
  if (isFamilyMemberContact) {
    const familyMember = memberDetails.members?.find((member) => member.customerId === custId);
    return {
      emailAddress: familyMember?.emailAddress || '',
      validityCode: '',
    };
  }

  return {
    emailAddress: data.emailAddress,
    validityCode: '',
  };
}

function getPhones(
  data: IContactInfoFormData,
  memberDetails: IMemberDetails,
  isFamilyMemberContact: boolean,
  custId?: string
): IChangeContactInfoRequest['contactInfo']['phone'] {
  if (isFamilyMemberContact) {
    const familyMember = memberDetails.members?.find((member) => member.customerId === custId);
    return familyMember?.phoneNumbers || [];
  }

  const phone = [];
  const { homePhone, mobilePhone, CLMobileConsent, HMMobileConsent, isHomePhoneDeclaredAsMobile = false } = data;

  const parsedHomePhoneNumber = homePhone ? homePhone.replaceAll(/[()\-\s]+/g, '') : '';

  let homePhoneMobileAuthStatus = MSSMobileAuthStatus.NOT_RESPONDED;
  if (parsedHomePhoneNumber && isHomePhoneDeclaredAsMobile && HMMobileConsent === 'agree') {
    homePhoneMobileAuthStatus = MSSMobileAuthStatus.CONSENTED;
  } else if (parsedHomePhoneNumber && isHomePhoneDeclaredAsMobile && HMMobileConsent === 'disagree') {
    homePhoneMobileAuthStatus = MSSMobileAuthStatus.NOT_CONSENTED;
  }

  const homePhoneObject: MSSPhone = {
    type: PhoneType.HOME,
    mobileAuthStatus: homePhoneMobileAuthStatus,
    isMobilePhone: isHomePhoneDeclaredAsMobile,
    areaCode: parsedHomePhoneNumber.slice(0, 3),
    prefix: parsedHomePhoneNumber.slice(3, 6),
    suffix: parsedHomePhoneNumber.slice(6, 10),
  };

  phone.push(homePhoneObject);

  // Mobile phone
  const rawMobilePhoneNumber = mobilePhone ? mobilePhone.replaceAll(/[()\-\s]+/g, '') : '';

  let mobilePhoneMobileAuthStatus;
  if (!mobilePhone) {
    mobilePhoneMobileAuthStatus = MSSMobileAuthStatus.NOT_RESPONDED;
  } else if (CLMobileConsent === 'agree') {
    mobilePhoneMobileAuthStatus = MSSMobileAuthStatus.CONSENTED;
  } else {
    mobilePhoneMobileAuthStatus = MSSMobileAuthStatus.NOT_CONSENTED;
  }

  const mobilePhoneObject: MSSPhone = {
    type: PhoneType.MOBILE,
    mobileAuthStatus: mobilePhoneMobileAuthStatus,
    isMobilePhone: true,
    areaCode: rawMobilePhoneNumber.slice(0, 3),
    prefix: rawMobilePhoneNumber.slice(3, 6),
    suffix: rawMobilePhoneNumber.slice(6, 10),
  };

  phone.push(mobilePhoneObject);

  return phone;
}
